/**
 * Site terminology.
 *
 * Different kinds of site call the same things by different names: a CT or
 * MR department says "scanner" where a mammography centre says "unit". One
 * site-wide setting (the `setting` row with key TERMINOLOGY_SETTING_KEY,
 * edited on the Settings page) picks a terminology, and the text the app
 * writes itself goes through `applyTerminology` on its way to the user: the
 * client's `term` pipe and TerminologyService, the server's e-mail templates
 * and the messages it writes for users.
 *
 * Two terminologies: "CT/MR", the wording the code is written in ("Select a
 * Scanner"), and "Universal", the wording that fits any modality ("Select a
 * Unit"). `applyTerminology` rewrites the code's wordings it finds in a
 * phrase, whole words only, keeping their capitalisation. With no setting,
 * or the CT/MR terminology, the text is returned untouched.
 *
 * Adding a difference: add the term to CODE_WORDING if it is not there yet,
 * and its replacement to every terminology that words it differently.
 * Then pass each phrase containing the word through the pipe / service; the
 * client spec core/terminology-templates.spec.ts lists the templates that
 * still show it unwrapped.
 *
 * Only text written in the code goes through it, never a string that holds a
 * name: a device called "Scanner 2" must stay "Scanner 2". Rewrite the
 * literal, then add the name.
 *
 * No dependencies, and compiled by the server's older TypeScript to es5: no
 * `replaceAll`, no regex lookbehind.
 */

/** key of the `setting` row that holds a site's terminology id */
export const TERMINOLOGY_SETTING_KEY = "terminology";

export interface Wording {
  /** singular, lower case */
  one: string;
  /** plural, lower case */
  many: string;
}

/** every term a terminology may reword, as the code writes it (the CT/MR wording) */
export const CODE_WORDING = {
  scanner: { one: "scanner", many: "scanners" },
  scanDate: { one: "scan date", many: "scan dates" },
};
export type Term = keyof typeof CODE_WORDING;

export interface Terminology {
  /** shown on the Settings page */
  label: string;
  /** the terms this terminology words differently; the rest stay as the code writes them */
  wording: { [term in Term]?: Wording };
}

export type TerminologyId = "ct_mr" | "universal";

export const TERMINOLOGIES: { [id in TerminologyId]: Terminology } = {
  ct_mr: { label: "CT/MR", wording: {} },
  universal: {
    label: "Universal",
    wording: {
      scanner: { one: "unit", many: "units" },
      scanDate: { one: "imaging date", many: "imaging dates" },
    },
  },
};

/** a site that never chose: the wording the code is written in, so nothing changes for it */
export const DEFAULT_TERMINOLOGY: TerminologyId = "ct_mr";

export const TERMINOLOGY_IDS = Object.keys(TERMINOLOGIES) as TerminologyId[];
const TERMS = Object.keys(CODE_WORDING) as Term[];

/** a stored setting value as a known id; anything else is the default */
export function toTerminologyId(value: unknown): TerminologyId {
  return typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(TERMINOLOGIES, value)
    ? (value as TerminologyId)
    : DEFAULT_TERMINOLOGY;
}

interface Rewrite {
  /** every code wording this terminology replaces, longest first */
  pattern: RegExp;
  /** code wording (lower case, single spaces) -> the site's wording */
  byCodeWording: { [codeWording: string]: string };
}

/** per terminology; null when it rewords nothing */
const rewrites: { [id: string]: Rewrite | null } = {};

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function rewriteFor(id: TerminologyId): Rewrite | null {
  if (rewrites[id] === undefined) {
    const byCodeWording: { [codeWording: string]: string } = {};
    const wording = TERMINOLOGIES[id].wording;
    TERMS.forEach((term) => {
      const site = wording[term];
      if (site) {
        byCodeWording[CODE_WORDING[term].one] = site.one;
        byCodeWording[CODE_WORDING[term].many] = site.many;
      }
    });
    // longest first, so "scan dates" is tried before "scan date"
    const codeWordings = Object.keys(byCodeWording).sort(
      (a, b) => b.length - a.length
    );
    rewrites[id] =
      codeWordings.length === 0
        ? null
        : {
            pattern: new RegExp(
              "\\b(" +
                codeWordings
                  .map((s) => escapeRegExp(s).replace(/ /g, "\\s+"))
                  .join("|") +
                ")\\b",
              "gi"
            ),
            byCodeWording,
          };
  }
  return rewrites[id];
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * The replacement in the capitalisation of what it replaces: "SCANNER" ->
 * "UNIT", "Scan Date" -> "Imaging Date", "Scan date" -> "Imaging date". Word
 * by word; capitals already in the replacement are kept.
 */
function inCaseOf(found: string, replacement: string): string {
  if (
    found.length > 1 &&
    found === found.toUpperCase() &&
    found !== found.toLowerCase()
  ) {
    return replacement.toUpperCase();
  }
  const foundWords = found.split(/\s+/);
  return replacement
    .split(" ")
    .map((word, i) => {
      const first = foundWords[Math.min(i, foundWords.length - 1)].charAt(0);
      return first !== first.toLowerCase() ? capitalise(word) : word;
    })
    .join(" ");
}

/**
 * `text` in a site's terminology: every whole-word occurrence of a code
 * wording the terminology rewords is replaced, in the capitalisation it was
 * written in. An unknown or missing id is the default terminology (CT/MR),
 * which returns the text as it is.
 */
export function applyTerminology(text: string, id?: string | null): string {
  if (typeof text !== "string" || text === "") {
    return text;
  }
  const rewrite = rewriteFor(toTerminologyId(id));
  if (!rewrite) {
    return text;
  }
  return text.replace(rewrite.pattern, (found) =>
    inCaseOf(
      found,
      rewrite.byCodeWording[found.toLowerCase().replace(/\s+/g, " ")]
    )
  );
}

/**
 * The terms as a terminology words them, capitalised, in CODE_WORDING
 * order: ["Scanner", "Scan date"] / ["Unit", "Imaging date"]. For the
 * Settings page, which must not pass them through the pipe.
 */
export function terminologyWords(id: TerminologyId): string[] {
  const wording = TERMINOLOGIES[id].wording;
  return TERMS.map((term) =>
    capitalise((wording[term] || CODE_WORDING[term]).one)
  );
}

/**
 * Every code wording that some terminology rewords (singular and plural).
 * Text containing one of these has to go through `applyTerminology`.
 */
export function rewordedCodeWordings(): string[] {
  const reworded: string[] = [];
  TERMS.forEach((term) => {
    if (TERMINOLOGY_IDS.some((id) => !!TERMINOLOGIES[id].wording[term])) {
      reworded.push(CODE_WORDING[term].one, CODE_WORDING[term].many);
    }
  });
  return reworded;
}
